// =============================================================================
// VandieZ shipper-web -- Disputes (/disputes)
// =============================================================================
// Flat feed of every dispute on the shipper's trips, newest first.
// Backed by GET /users/me/disputes (added 2026-05-31). The previous
// "enumerate terminal trips and drill in" pattern is gone — operators
// see the whole feed in one shot and can filter by status.
//
// To FILE a new dispute the shipper still drills into a specific trip
// via /disputes/:tripId (per-trip raise form lives there).
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Scale,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { disputesApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { DisputeRead, DisputeStatus } from "@/types/dispute";

const PAGE_SIZE = 50;

type FilterValue = DisputeStatus | "all";

const STATUS_FILTERS: { label: string; value: FilterValue }[] = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "Under review", value: "under_review" },
  { label: "Resolved", value: "resolved" },
  { label: "Rejected", value: "rejected" },
];

const STATUS_PILL: Record<DisputeStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  under_review: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-muted text-muted-foreground",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function DisputesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<DisputeRead[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const filter = (searchParams.get("status") as FilterValue | null) ?? "all";
  const offset =
    Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;

  const updateFilters = useCallback(
    (next: { status?: string; offset?: number }): void => {
      setSearchParams(
        (cur) => {
          const p = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (v === undefined || v === "" || v === "all" || v === 0) {
              p.delete(k);
            } else {
              p.set(k, String(v));
            }
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await disputesApi.listMine(
          accessToken,
          {
            limit: PAGE_SIZE,
            offset,
            ...(filter !== "all" ? { status: filter } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load disputes.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filter, offset],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchPage(c.signal);
    return () => { c.abort(); };
  }, [fetchPage]);

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Disputes"
        description="Disputes you've raised or that have been raised against your trips. To file a new dispute, open the trip and use the dispute form there."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" /> My disputes
          </CardTitle>
          <CardDescription>
            Filter by status. Click a row to open the trip-scoped detail
            view where you can add evidence or read the admin's
            resolution notes.
            {total > 0 && (
              <span className="ml-1 text-foreground">
                Showing {offset + 1}–{offset + items.length} of {total}.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => { updateFilters({ status: f.value, offset: 0 }); }}
                className={cn(
                  "rounded px-3 py-1 text-sm",
                  filter === f.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && items.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No disputes match this filter. To raise a new dispute on a
              completed or cancelled trip, open the trip detail page and
              use the dispute form (7-day window after the trip ends).
            </div>
          ) : (
            <>
              {/* Mobile (<md): stacked cards, one per dispute. Same data
                  as the desktop table but flowed vertically so it fits a
                  375 px viewport without horizontal scroll. */}
              <ul className="space-y-2 md:hidden">
                {items.map((d) => (
                  <li key={d.id}>
                    <Link
                      to={`/disputes/${d.trip_id}`}
                      className="block rounded-md border bg-background p-3 transition-colors hover:border-primary/50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium capitalize">
                            {d.category.replace(/_/g, " ")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Raised {formatDate(d.raised_at)} ·{" "}
                            <span className="capitalize">{d.raised_by_role}</span>
                          </p>
                          <p className="font-mono text-xs text-muted-foreground">
                            Trip {d.trip_id.slice(0, 8)}…
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                              STATUS_PILL[d.status],
                            )}
                          >
                            {d.status.replace(/_/g, " ")}
                          </span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* md+: original table layout (still wrapped in overflow-x
                  for safety on tablets between md and lg). */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Raised</th>
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4">Raised by</th>
                      <th className="py-2 pr-4">Trip</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((d) => (
                      <tr key={d.id} className="border-b last:border-b-0 align-top">
                        <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(d.raised_at)}
                        </td>
                        <td className="py-3 pr-4 capitalize">
                          {d.category.replace(/_/g, " ")}
                        </td>
                        <td className="py-3 pr-4 capitalize text-xs text-muted-foreground">
                          {d.raised_by_role}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          <Link
                            to={`/trips/${d.trip_id}`}
                            className="hover:underline"
                          >
                            {d.trip_id.slice(0, 8)}…
                          </Link>
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                              STATUS_PILL[d.status],
                            )}
                          >
                            {d.status.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <Link
                            to={`/disputes/${d.trip_id}`}
                            className="text-primary hover:underline"
                          >
                            Open <ChevronRight className="inline h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev || loading}
              onClick={() => { updateFilters({ offset: Math.max(0, offset - PAGE_SIZE) }); }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            <span className="hidden sm:inline text-xs text-muted-foreground">
              {loading ? "Loading…" : `Page ${Math.floor(offset / PAGE_SIZE) + 1}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext || loading}
              onClick={() => { updateFilters({ offset: offset + PAGE_SIZE }); }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
